// source: template-project@7007a338a apps/backoffice/src/app-shell/{AppV3Sidebar,AppShellSidebar,RecentChatsNav}.tsx +
//         components/sidebar/{SidebarBrandSwitcher,NavUser,NavMenuItem,CollapsibleNavItem}.tsx +
//         app-shell/MoreToolsNav.tsx + ui/shadcn/sidebar.tsx (class strings only; static markup, no radix, no router)
import { ChevronDown, ChevronRight, ChevronsUpDown, PanelLeft, Plus, User } from 'lucide-react';

import { Button } from '@/components/ui/shadcn/button';
import { initialsOf } from '@/lib/operator-identity';
import { cn } from '@/lib/utils';

import { MORE_TOOLS_ENTRY, NAV_ENTRIES, ONBOARDING_ROOM_TITLE } from './blurred-sidebar-nav';

/**
 * The sidebar's text at 13px with the v3 row heights, applied by `data-sidebar` selector to the rows below
 * (verbatim TP COMPACT_NAV_CLASS in AppV3Sidebar.tsx).
 */
export const COMPACT_NAV_CLASS =
  '[&_[data-sidebar=content]_a]:text-[13px] [&_[data-sidebar=content]_button]:text-[13px] [&_[data-sidebar=header]_span]:text-[13px] [&_[data-sidebar=footer]_span]:text-[13px] [&_[data-sidebar=content]]:gap-0.5 [&_[data-sidebar=content]]:py-2 [&_[data-sidebar=content]_[data-sidebar=group]]:py-0 [&_[data-sidebar=content]_[data-sidebar=menu-button]]:h-[34px] [&_[data-sidebar=content]_[data-nav-row]]:h-[34px] [&_[data-sidebar=content]_[data-sidebar=menu-sub-button]]:h-[30px]';

/** TP sidebarMenuButtonVariants (default variant, default size), without the icon-collapse parts. */
const MENU_BUTTON_CLASS =
  'peer/menu-button group/menu-button flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm ring-sidebar-ring outline-none data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0 [&>span:last-child]:truncate';

/** The status dots of TP's SidebarStatusDot that a pinned room can show here (STATUS_DOT_CLASS). */
export type RoomStatus = 'open' | 'completed';
const ROOM_STATUS_DOT_CLASS: Record<RoomStatus, string> = {
  open: 'bg-current opacity-25',
  completed: 'bg-green',
};
const ROOM_STATUS_LABEL: Record<RoomStatus, string> = { open: 'Open', completed: 'Completed' };

/**
 * React 18's types do not know `inert`; set as a string attribute it reaches the DOM, where it removes the
 * subtree from focus, find-in-page and the accessibility tree.
 */
const INERT_ATTRIBUTE = { inert: '' } as Record<string, string>;

/** The rows are `button`s, as TP's are `a`/`button`, so COMPACT_NAV_CLASS's element selectors size them. */
const ROW_BUTTON_PROPS = { type: 'button', tabIndex: -1 } as const;

/** TP's TaskStatusDot (RecentChatsNav) over SidebarStatusDot (SidebarStatusGroups). */
function TaskStatusDot({ status }: { status: RoomStatus }) {
  return (
    <span className="flex size-4 shrink-0 items-center justify-center">
      <span
        aria-hidden="true"
        data-status-dot={status}
        className={cn('size-2 shrink-0 rounded-full', ROOM_STATUS_DOT_CLASS[status])}
      />
      <span className="sr-only">{ROOM_STATUS_LABEL[status]}</span>
    </span>
  );
}

/** The brand the switcher shows before there is a prospect brand (TP's own fallback: the product's name). */
const SWITCHER_FALLBACK_BRAND = 'TrafiFlow';

export interface BlurredAppSidebarProps {
  /** The prospect's brand in the switcher row, once the chat knows it. Without one: "TrafiFlow" (TP's fallback). */
  brandName?: string;
  /** The account row's first line: the signed-in operator's name; without it the e-mail. With neither, no account row. */
  userName?: string;
  /** The signed-in e-mail: the first line when there is no name, else the second line after the role. */
  userEmail?: string;
  /** The operator's role (from their Cognito groups), the second line's first part. Absent when no group has a label. */
  userRole?: string;
  /** How far the onboarding room is: open (grey dot) or completed (green). Wave 6 wires it from the tasks. */
  roomStatus?: RoomStatus;
  className?: string;
}

/**
 * The /app-v3 sidebar as a backdrop: brand switcher, the nine-entry menu with More Tools,
 * a Rooms group holding only the pinned Onboarding room, and the account row. It mirrors TP's AppV3Sidebar
 * with its own class strings and `data-sidebar` hooks, so it matches by construction. It is static, blurred
 * and unreachable: `aria-hidden`, `inert`, `pointer-events-none`, no links, no handlers.
 */
export function BlurredAppSidebar({
  brandName,
  userName,
  userEmail,
  userRole,
  roomStatus = 'open',
  className,
}: BlurredAppSidebarProps) {
  const brand = brandName?.trim() || SWITCHER_FALLBACK_BRAND;
  // Only what the login session gave: the name (else the e-mail), then the role and, when the name is not the e-mail, the e-mail.
  const primaryLabel = userName?.trim() || userEmail?.trim() || null;
  const secondaryLabel =
    [userRole, userEmail && userEmail !== primaryLabel ? userEmail : null].filter(Boolean).join(' · ') || null;
  const initials = primaryLabel ? initialsOf(primaryLabel) : '';
  const MoreToolsIcon = MORE_TOOLS_ENTRY.icon;
  return (
    <div
      aria-hidden="true"
      {...INERT_ATTRIBUTE}
      data-testid="blurred-app-sidebar"
      data-sidebar="sidebar"
      className={cn(
        'pointer-events-none flex size-full select-none flex-col blur-[6px] [background:var(--sidebar-bg)]',
        COMPACT_NAV_CLASS,
        className,
      )}
    >
      <div data-sidebar="header" className="flex flex-col gap-0 p-2 pb-3 pt-[9px]">
        <div className="group/logo-row relative flex items-center gap-1">
          <ul className="flex w-full min-w-0 flex-1 flex-col gap-0.5">
            <li className="group/menu-item relative">
              <span className={cn(MENU_BUTTON_CLASS, 'h-8 gap-2.5')}>
                <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-xs font-semibold text-sidebar-accent-foreground">
                  {brand.trim().charAt(0).toUpperCase() || '?'}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-sidebar-foreground">{brand}</span>
              </span>
            </li>
          </ul>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            tabIndex={-1}
            aria-label="Collapse sidebar"
            className="text-sidebar-foreground/60"
          >
            <PanelLeft />
          </Button>
        </div>
      </div>

      <div data-sidebar="content" className="no-scrollbar flex min-h-0 flex-1 flex-col gap-0 overflow-hidden">
        <div data-sidebar="group" className="relative flex w-full min-w-0 flex-col p-2">
          <div data-sidebar="group-content" className="w-full text-sm">
            <ul data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-0.5">
              {NAV_ENTRIES.map(({ label, icon: Icon, kind }) => (
                <li key={label} className="group/menu-item relative">
                  {kind === 'group' ? (
                    <div className="group/nav-row relative flex w-full items-center gap-1 rounded-md">
                      <button
                        {...ROW_BUTTON_PROPS}
                        data-nav-row=""
                        className="flex h-8 flex-1 items-center gap-2 overflow-hidden rounded-md p-2 text-left text-body-sm outline-none"
                      >
                        <Icon className="size-4 shrink-0" />
                        <span className="flex-1 truncate">{label}</span>
                      </button>
                      <span className="mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded text-sidebar-foreground/60">
                        <ChevronDown className="size-4" />
                      </span>
                    </div>
                  ) : (
                    // Leaf and hub rows are text-only while expanded: TP hides their leading icon.
                    <button
                      {...ROW_BUTTON_PROPS}
                      data-sidebar="menu-button"
                      data-active="false"
                      data-nav-kind={kind}
                      className={MENU_BUTTON_CLASS}
                    >
                      <span>{label}</span>
                      {kind === 'hub' ? <ChevronRight className="ml-auto size-3.5 text-ds-text-2" /> : null}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* TP MoreToolsNav: its own group, one row. */}
        <div data-sidebar="group" className="relative flex w-full min-w-0 flex-col p-2">
          <div data-sidebar="group-content" className="w-full text-sm">
            <ul data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-0.5">
              <li className="group/menu-item relative">
                <button {...ROW_BUTTON_PROPS} data-sidebar="menu-button" data-active="false" className={MENU_BUTTON_CLASS}>
                  <MoreToolsIcon className="size-4" />
                  <span>{MORE_TOOLS_ENTRY.label}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* TP RecentChatsNav tasksOnly: the "Rooms" group, className mt-5, with only the pinned room. */}
        <div data-sidebar="group" className="relative mt-5 flex w-full min-w-0 flex-col p-2 pt-0">
          <div className="flex h-7 items-center pl-2 pr-1">
            <button
              {...ROW_BUTTON_PROPS}
              aria-expanded
              className="flex items-center gap-1 rounded-md text-xs font-medium text-sidebar-foreground/70 outline-none"
            >
              Rooms
              <ChevronDown className="size-3.5" aria-hidden="true" />
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              tabIndex={-1}
              aria-label="New room"
              className="ml-auto size-6 text-sidebar-foreground/70"
            >
              <Plus aria-hidden="true" />
            </Button>
          </div>
          <div data-sidebar="group-content" className="w-full text-sm">
            <ul data-sidebar="menu" className="flex w-full min-w-0 flex-col gap-0.5">
              <li className="group/menu-item relative">
                <button
                  {...ROW_BUTTON_PROPS}
                  data-sidebar="menu-button"
                  data-active="true"
                  data-room="onboarding"
                  className={MENU_BUTTON_CLASS}
                >
                  <TaskStatusDot status={roomStatus} />
                  <span className="truncate">{ONBOARDING_ROOM_TITLE}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div data-sidebar="footer" className="flex flex-col gap-2 p-2">
        {primaryLabel ? (
          <ul className="flex w-full min-w-0 flex-col gap-0.5">
            <li className="group/menu-item relative">
              <span className={cn(MENU_BUTTON_CLASS, 'h-12')}>
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-foreground text-xs font-semibold text-background">
                  {initials || <User aria-hidden="true" className="size-4" />}
                </span>
                <span className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-label text-sidebar-foreground">{primaryLabel}</span>
                  {secondaryLabel ? <span className="truncate text-meta text-ds-text-2">{secondaryLabel}</span> : null}
                </span>
                <ChevronsUpDown className="ml-auto size-4 text-sidebar-foreground/60" />
              </span>
            </li>
          </ul>
        ) : null}
      </div>
    </div>
  );
}
